import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Autocomplete, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormLabel, Stack, TextField } from '@mui/material';
import { Modal } from '../dialogs/useModal';
import { RichTextField, htmlIsEmpty } from '../components/richText';
import { TermlexDefinitions } from './TermlexDefinitions';
import { GET_ALL as GET_CATEGORIES } from '../categories/categories.graphql';
import { TermItemFragment } from '../__generated__/graphql';
import { ADD, GET_ALL, UPDATE } from './dictionary.graphql';

export interface TermDialogInput {
  term?: TermItemFragment;
}

type CategoryOption = { id: string; title?: string | null };
type TermOption = { id: string; preferred?: string | null };

/** Add or edit a term. Clicking a synonym swaps it with the ja-ord («klosett» → «toalett»). */
export function TermDialog({ modal }: { modal: Modal<TermDialogInput, string | undefined> }) {
  const term = modal.data.term;
  const [preferred, setPreferred] = useState(term?.preferred ?? '');
  const [synonyms, setSynonyms] = useState((term?.synonyms ?? []).filter((x): x is string => !!x));
  const [categories, setCategories] = useState<CategoryOption[]>((term?.categories ?? []).filter((x): x is CategoryOption => !!x));
  const [seeAlso, setSeeAlso] = useState<TermOption[]>((term?.seeAlso ?? []).filter((x): x is TermOption => !!x));
  const [definition, setDefinition] = useState(term?.definition ?? '');
  const [notes, setNotes] = useState(term?.notes ?? '');
  const [error, setError] = useState<string>();

  const { data: categoryData } = useQuery(GET_CATEGORIES);
  const { data: termData } = useQuery(GET_ALL);
  const refetchQueries = [{ query: GET_ALL }];
  const [add, { loading: adding }] = useMutation(ADD, { refetchQueries });
  const [update, { loading: updating }] = useMutation(UPDATE, { refetchQueries });

  const swap = (word: string) => {
    setSynonyms(synonyms.map((s) => (s === word ? preferred : s)).filter((s) => !!s.trim()));
    setPreferred(word);
  };

  const handleOk = async () => {
    setError(undefined);
    const fields = {
      preferred,
      synonyms,
      categoryIds: categories.map((c) => c.id),
      seeAlso: seeAlso.map((t) => t.id),
      definition,
      notes,
    };
    const result = term
      ? (await update({ variables: { input: { id: term.id, ...fields } } })).data?.termUpdate
      : (await add({ variables: { input: fields } })).data?.termAdd;
    const message = result?.errors?.map((e) => ('message' in e ? e.message : '')).join(' ');
    if (message) {
      setError(message);
      return;
    }
    modal.close(result?.termModel?.id);
  };

  const saving = adding || updating;
  return (
    <Dialog open={true} onClose={() => modal.close()} fullWidth maxWidth="md">
      <DialogTitle>{term ? 'Endre begrep' : 'Nytt begrep'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Begrep (vi velger)" value={preferred} onChange={(e) => setPreferred(e.target.value)} autoFocus fullWidth required />
          <SynonymsField value={synonyms} onChange={setSynonyms} onSwap={swap} />
          <Autocomplete
            multiple
            options={(categoryData?.categories ?? []).filter(Boolean) as CategoryOption[]}
            value={categories}
            onChange={(_e, v) => setCategories(v)}
            getOptionLabel={(o) => o.title ?? ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Fagområder" />}
          />
          <Autocomplete
            multiple
            options={(termData?.terms ?? [])
              .flatMap((t): TermOption[] => (t && t.id !== term?.id ? [{ id: t.id, preferred: t.preferred }] : []))
              .sort((a, b) => (a.preferred ?? '').localeCompare(b.preferred ?? ''))}
            value={seeAlso}
            onChange={(_e, v) => setSeeAlso(v)}
            getOptionLabel={(o) => o.preferred ?? ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Se også" />}
          />
          <TermlexDefinitions term={preferred} hasDefinition={!htmlIsEmpty(definition)} onUse={setDefinition} />
          <div>
            <FormLabel>Definisjon</FormLabel>
            <RichTextField value={definition} onChange={setDefinition} />
          </div>
          <div>
            <FormLabel>Merknad om bruk</FormLabel>
            <RichTextField value={notes} onChange={setNotes} />
          </div>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => modal.close()}>Avbryt</Button>
        <Button onClick={handleOk} variant="contained" color="primary" disabled={saving || !preferred.trim()}>
          {saving ? 'Lagrer...' : term ? 'Endre' : 'Legg til'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Synonyms as chips. Typing a word and pressing Enter (or leaving the field) adds it. */
function SynonymsField({ value, onChange, onSwap }: { value: string[]; onChange: (v: string[]) => void; onSwap: (word: string) => void }) {
  return (
    <Autocomplete
      multiple
      freeSolo
      autoSelect
      options={[]}
      value={value}
      onChange={(_e, v) => onChange(v.map((x) => x.trim()).filter((x, i, all) => !!x && all.indexOf(x) === i))}
      renderTags={(words, getTagProps) =>
        words.map((word, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} {...tagProps} label={word} onClick={() => onSwap(word)} title="Klikk for å gjøre til begrep" />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Alternativ (vi velger ikke)"
          helperText="Andre ord for begrepet. Trykk Enter for å legge til. Klikk på et alternativ for å bytte det med begrepet."
        />
      )}
    />
  );
}
